import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Cpu, Pencil } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";

function statusBadge(status) {
  const map = {
    available: "bg-slate-100 text-slate-700 border-slate-200",
    assigned: "bg-emerald-100 text-emerald-800 border-emerald-200",
    offline: "bg-red-100 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={`${map[status] || map.available} text-[10px] uppercase tracking-[0.12em] font-semibold`}>{status}</Badge>;
}

export default function AdminHeadsets() {
  const [headsets, setHeadsets] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ serial: "", name: "", model: "Quest 3", notes: "", test: false });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirmDialog = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [h, u] = await Promise.all([api.get("/headsets"), api.get("/users")]);
      setHeadsets(h.data);
      setClinicians(u.data.filter((x) => x.role === "therapist"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/headsets/${editing.id}`, form);
        toast.success("Headset updated");
      } else {
        await api.post("/headsets", form);
        toast.success("Headset registered");
      }
      setOpen(false);
      setEditing(null);
      setForm({ serial: "", name: "", model: "Quest 3", notes: "", test: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({ serial: h.serial || "", name: h.name || "", model: h.model || "", notes: h.notes || "", test: !!h.test });
    setOpen(true);
  };
  const openCreate = () => {
    setEditing(null);
    setForm({ serial: "", name: "", model: "Quest 3", notes: "", test: false });
    setOpen(true);
  };

  const reassign = async (headsetId, clinicianId) => {
    try {
      await api.patch(`/headsets/${headsetId}/assign-clinician`, { clinician_id: clinicianId || null });
      toast.success("Assignment updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (h) => {
    const ok = await confirmDialog({
      title: "Delete headset?",
      description: `Permanently remove ${h.name || h.serial} and all its module assignments. Telemetry history is retained.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/headsets/${h.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Inventory"
        title="Headsets"
        description="Register VR devices and assign them to therapists. Therapists then assign their headsets to patients."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="register-headset-button" onClick={openCreate} className="btn-brand rounded-full">
                <Plus className="h-4 w-4 mr-2" /> Register Headset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Edit headset" : "Register headset"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Serial</Label>
                  <Input data-testid="headset-serial-input" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Model</Label>
                  <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 p-2">
                  <div><div className="text-sm font-medium">Test headset</div>
                    <div className="text-xs text-slate-500">Events are stored but never change levels, notify or count toward adherence.</div></div>
                  <Switch checked={!!form.test} onCheckedChange={(v) => setForm({ ...form, test: v })} data-testid="headset-test-switch" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Notes</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
                <Button data-testid="headset-submit-button" onClick={submit} className="btn-brand rounded-full">{editing ? "Save" : "Register"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="panel rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-purple-50/50">
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Name / Serial</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Model</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Assigned Clinician</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Patient</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            : headsets.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-16"><Cpu className="h-8 w-8 text-slate-300 mx-auto mb-2" /><div className="text-sm text-slate-500">No headsets registered</div></TableCell></TableRow>
            : headsets.map((h) => (
              <TableRow key={h.id} data-testid={`headset-row-${h.id}`} className="hover:bg-slate-50/60">
                <TableCell>
                  <div className="text-sm font-medium">
                    {h.name || <span className="text-slate-400 italic">Unnamed</span>}
                    {h.test ? <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-[0.12em] bg-sky-50 text-sky-800 border-sky-200" data-testid={`test-tag-${h.id}`}>test</Badge> : null}
                    {h.auto_registered && !h.clinician_id ? <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-[0.12em] bg-amber-50 text-amber-800 border-amber-200">New</Badge> : null}
                  </div>
                  <div className="font-mono text-[11px] text-slate-500 break-all max-w-[260px]">{h.serial}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{h.model}</TableCell>
                <TableCell>{statusBadge(h.status)}</TableCell>
                <TableCell>
                  <Select value={h.clinician_id || "none"} onValueChange={(v) => reassign(h.id, v === "none" ? null : v)}>
                    <SelectTrigger data-testid={`headset-clinician-select-${h.id}`} className="h-8 text-xs min-w-[180px]">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {clinicians.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{h.patient?.name || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(h)} data-testid={`headset-edit-${h.id}`}>
                    <Pencil className="h-4 w-4 text-slate-400 hover:text-purple-700" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(h)} data-testid={`headset-delete-${h.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
